import React from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Empty, Alert, Space } from "antd";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Avatar } from "../primitives";
import { RollupBars } from "./charts";
import {
  TakeawayList, StretchAnalysis, WorkLogSkeleton, flattenStretches,
} from "./WorkLog";
import * as api from "./drilldownApi";

/**
 * MemberInProject — the third tier of the drill-down.
 *
 * Reached from the project drawer: project → its people → THIS person's work on
 * THIS project. Until now the project drawer's People tab was a dead-end table;
 * a row told you someone spent 12h on a project and gave you no way to ask what
 * those 12h were.
 *
 * It reuses the person work-log surface with `project` scope, so the answer here
 * and the answer in the person drawer come from one code path and cannot drift.
 *
 * Honesty carried through from the service:
 *   · the day headline is withheld under a project filter — the stored headline
 *     describes the whole DAY, and passing it off as a summary of this project's
 *     slice would be a lie;
 *   · a person with tracked hours on the project but no written narrative gets
 *     an explicit explanation, not an empty panel.
 */
export function MemberInProject({ member, project, range, onBack, onOpenPerson }) {
  const t = useDashTheme();
  const [worklog, setWorklog] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [focus, setFocus] = React.useState(null);

  const userId = member && (member.userId || member.id);
  const name = api.memberName(member);

  React.useEffect(() => {
    if (!userId) return undefined;
    const ac = new AbortController();
    let alive = true;
    setLoading(true); setError(null); setWorklog(null); setFocus(null);

    api.personWorklog(userId, range, { signal: ac.signal, project: project?.name })
      .then((w) => { if (alive) setWorklog(w); })
      .catch((e) => {
        if (alive && e.name !== "CanceledError" && e.name !== "AbortError") {
          setError(e.message || "Could not load this member's work on the project.");
        }
      })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; ac.abort(); };
  }, [userId, range, project?.name]);

  const stretches = React.useMemo(() => flattenStretches(worklog), [worklog]);
  const trackedMins = Math.round((Number(member?.totalHours) || 0) * 60);
  const accounted = worklog?.totalMinutes || 0;
  /* Hours the tracker recorded against this project that the narrative does not
     account for. Stated rather than hidden — it is usually the honest majority. */
  const unaccounted = Math.max(0, trackedMins - accounted);

  return (
    <motion.div
      initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.22, ease: t.motion.ease.out }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <TextBtn t={t} onClick={onBack}><ChevronLeft size={14} /> All contributors</TextBtn>
        <span style={{ flex: 1 }} />
        {onOpenPerson && (
          <TextBtn t={t} onClick={() => onOpenPerson({ id: String(userId), name })}>
            Open {name.split(" ")[0]}’s full drill-down <ExternalLink size={13} />
          </TextBtn>
        )}
      </div>

      {/* Same head anatomy as every drill (2026-08-30): tone rail · tile ·
          kicker · title · chips. The three antd Statistics it replaced were
          the only KPI style of their kind in the product. */}
      <div style={{ position: "relative", overflow: "hidden", display: "flex", gap: 14, alignItems: "flex-start", padding: "16px 18px 14px", borderRadius: t.r.card, background: t.c.surface, border: `1px solid ${t.c.border}` }}>
        <span aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: t.c.ai }} />
        <span aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(420px 140px at 96% -50%, ${t.c.ai}18, transparent 62%)` }} />
        <span style={{ position: "relative", width: 46, height: 46, borderRadius: 13, flex: "none", display: "grid", placeItems: "center", overflow: "hidden", background: `${t.c.ai}18`, border: `1px solid ${t.c.ai}3a` }}>
          <Avatar user={member} name={name} size={46} />
        </span>
        <div style={{ position: "relative", minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: t.c.ai }}>Person · on a project</div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.4px", color: t.c.text, marginTop: 3 }}>{name}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            {[
              { label: project?.name, dot: t.c.ai },
              { label: `${member?.totalHours != null ? fmt(Math.round(Number(member.totalHours) * 60)) : "—"} on it` },
              { label: `${member?.totalActiveTimeFormatted || "0h 0m"} active`, tone: t.status.positive },
              member?.source === "agent" ? { label: "no session detail", tone: t.status.warning } : { label: `${member?.sessionCount ?? 0} session${member?.sessionCount === 1 ? "" : "s"}` },
              member?.offTeamRoster ? { label: "not on roster", tone: t.status.warning } : null,
            ].filter((c) => c && c.label).map((c, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", fontSize: 12.5, fontWeight: 650, padding: "6px 13px", borderRadius: 999, background: c.tone ? `${c.tone}1f` : t.c.surfaceMuted, border: `1px solid ${c.tone ? `${c.tone}55` : t.c.border}`, color: c.tone || t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                {c.dot ? <i style={{ width: 7, height: 7, borderRadius: 99, background: c.dot }} /> : null}{c.label}
              </span>
            ))}
          </div>
        </div>
      </div>
      {member?.source === "agent" && (
        <div style={{ marginTop: 10 }}>
          <Alert
            type="info" showIcon
            message="No session detail for this member"
            description="Their time on this project was not captured as start/stop sessions, so the session count is absent rather than zero. The hours are real."
            style={{ fontSize: 12.5 }}
          />
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <SecLabel t={t} hint={worklog?.coverage?.label}>
          {worklog?.scope?.fallback === "person"
            ? `What ${name.split(" ")[0]} worked on this period`
            : `What ${name.split(" ")[0]} did on ${project?.name}`}
        </SecLabel>

        {worklog?.scope?.fallback === "person" && !loading && !error && (
          /* The tracker attributes hours by project id; the written analysis
             attributes stretches by NAME — and often cannot name one at all.
             Showing the person's full log, said out loud, beats an empty panel
             sitting over ${'{'}fmt(trackedMins){'}'} of real work. */
          <Alert
            type="info" showIcon style={{ marginBottom: 12, fontSize: 12.5 }}
            message={`Nothing was written against ${project?.name} by name`}
            description={`Showing everything ${name.split(" ")[0]} worked on in this period instead — the tracker attributed ${fmt(trackedMins)} of it to this project. ${worklog?.coverage?.label || ""}`}
          />
        )}
        {loading ? (
          <WorkLogSkeleton />
        ) : error ? (
          <Alert type="error" showIcon message="Couldn’t load the work log" description={error} />
        ) : focus !== null && stretches[focus] ? (
          <StretchAnalysis
            userId={userId}
            stretch={stretches[focus]}
            index={focus}
            total={stretches.length}
            timeline={null}
            onBack={() => setFocus(null)}
            onJump={setFocus}
            onFullLog={() => setFocus(null)}
          />
        ) : stretches.length ? (
          <>
            <TakeawayList worklog={worklog} focusIndex={focus} onPick={setFocus} />

            {unaccounted > 0 && worklog?.scope?.fallback !== "person" && (
              <Alert
                type="warning" showIcon
                style={{ marginTop: 8, fontSize: 12.5 }}
                message={`${fmt(unaccounted)} of tracked project time has no written stretch behind it`}
                description="The tracker attributed those hours to this project; the summary pipeline did not describe them. They are counted above and deliberately not invented here."
              />
            )}

            {worklog.rollup?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <SecLabel t={t} hint="on this project only">Where the effort went</SecLabel>
                <RollupBars rows={worklog.rollup} />
              </div>
            )}
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 13, color: t.c.textTertiary, lineHeight: 1.6 }}>
                {trackedMins > 0 ? (
                  <>
                    <b style={{ color: t.c.text }}>{fmt(trackedMins)} tracked on this project, but nothing written about it.</b>
                    <br />
                    Either the summary pipeline has not run for these days, or the writer attributed
                    the work to a different project name than the tracker did.
                  </>
                ) : (
                  <>No tracked time for {name.split(" ")[0]} on this project in this window.</>
                )}
              </span>
            }
          />
        )}
      </div>
    </motion.div>
  );
}

/** Project drawer → clickable contributor list. Replaces the dead-end table. */
export function ContributorList({ related, onPick }) {
  const t = useDashTheme();
  const rows = related?.memberHours || [];
  if (!rows.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tracked member time on this project." />;
  }
  const max = Math.max(...rows.map((m) => Number(m.totalHours) || 0), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((m) => {
        const name = api.memberName(m);
        const hours = Number(m.totalHours) || 0;
        return (
          <button
            key={String(m.userId)}
            onClick={() => onPick(m)}
            style={{
              display: "grid", gridTemplateColumns: "auto 1fr auto auto", alignItems: "center", gap: 12,
              width: "100%", padding: "11px 13px", borderRadius: t.r.md,
              background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`,
              border: "none", cursor: "pointer", textAlign: "left", fontFamily: t.font.family,
            }}
          >
            <Avatar user={m} name={name} size={32} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: t.c.text }}>{name}</span>
              <Space size={6} style={{ marginTop: 3 }}>
                <span style={{ fontSize: 11.5, color: t.c.textTertiary }}>
                  {m.source === "agent" ? "no sessions" : `${m.sessionCount || 0} session${m.sessionCount === 1 ? "" : "s"}`}
                  {m.totalActiveTimeFormatted ? ` · ${m.totalActiveTimeFormatted} active` : ""}
                </span>
              </Space>
            </span>
            <span style={{ width: 96, height: 6, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
              <span style={{ display: "block", height: "100%", borderRadius: 99, width: `${Math.max(3, (hours / max) * 100)}%`, background: t.c.primary }} />
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <b style={{ fontSize: 13, fontVariantNumeric: "tabular-nums", color: t.c.text }}>{hours}h</b>
              <ChevronRight size={15} style={{ color: t.c.textTertiary }} />
            </span>
          </button>
        );
      })}
      <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 2 }}>
        Click a contributor to see what they actually did on this project.
      </div>
    </div>
  );
}

/* ── shared bits ─────────────────────────────────────────────────────────── */
const fmt = (m) => {
  const v = Math.max(0, Math.round(m));
  const h = Math.floor(v / 60);
  return h ? `${h}h ${String(v % 60).padStart(2, "0")}m` : `${v}m`;
};

function SecLabel({ t, children, hint }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
      ...t.font.microLabel,
      color: t.c.textTertiary, marginBottom: 10,
    }}>
      <span>{children}</span>
      {hint && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>{hint}</span>}
    </div>
  );
}

function TextBtn({ t, children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 8px",
        border: "none", borderRadius: t.r.sm, background: "none", color: t.c.primary,
        fontSize: 12.5, fontWeight: 500, fontFamily: t.font.family, cursor: "pointer",
      }}
    >{children}</button>
  );
}

export default MemberInProject;
